<template>
  <div class="sclaims">
    <section class="sclaims__kpis" data-tour="seller-claims-kpis">
      <article v-for="card in summary" :key="card.label" class="sui-card sclaims__kpi">
        <p class="sclaims__kpi-label">{{ card.label }}</p>
        <p class="sclaims__kpi-value">{{ card.value }}<em>{{ card.unit }}</em></p>
        <p class="sclaims__kpi-desc">{{ card.desc }}</p>
      </article>
    </section>

    <section class="sui-card sui-card--flush">
      <div class="sui-tabs" data-tour="seller-claims-tabs">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          type="button"
          class="sui-tab"
          :class="{ 'is-active': tab.key === activeTab }"
          @click="setActiveTab(tab.key)"
        >
          {{ tab.label }} <em>{{ tab.count }}</em>
        </button>
      </div>

      <div class="sui-toolbar sclaims__toolbar" data-tour="seller-claims-toolbar">
        <div class="sui-presets" role="group" aria-label="기간">
          <button
            v-for="item in presets"
            :key="item.key"
            type="button"
            :class="{ 'is-active': preset === item.key }"
            @click="applyPreset(item.key)"
          >
            {{ item.label }}
          </button>
        </div>
        <label class="sui-date">
          <span>시작일</span>
          <input v-model="fromDate" type="date" @change="onManualDateChange" />
        </label>
        <label class="sui-date">
          <span>종료일</span>
          <input v-model="toDate" type="date" @change="onManualDateChange" />
        </label>
        <label class="sui-search">
          <AppIcon icon="mdi-magnify" :size="17" />
          <input v-model.trim="keyword" type="search" placeholder="주문번호 · 상품명 · 기관명 검색" />
        </label>
        <span class="sui-toolbar__spacer" />
        <button type="button" class="sui-btn" :disabled="loading" @click="load">
          <AppIcon icon="mdi-refresh" :size="16" />
          새로고침
        </button>
      </div>

      <p v-if="errorMessage" class="sclaims__error">
        <AppIcon icon="mdi-alert-outline" :size="16" />
        {{ errorMessage }}
      </p>

      <ul v-if="filtered.length" class="sclaims__list" data-tour="seller-claims-list">
        <li v-for="item in filtered" :key="item.idx" class="sclaims__item">
          <div class="sclaims__item-head">
            <span
              class="sui-badge"
              :class="isRejected(item) ? 'sui-badge--gray' : statusTone(item.status)"
            >
              {{ isRejected(item) ? '반려' : MALL_ORDER_ITEM_STATUS_LABEL[item.status] }}
            </span>
            <span class="sclaims__order-id">{{ item.orderCode }}</span>
            <span class="sclaims__pay" :class="{ 'is-card': item.payMethod === 'TOSS' }">{{ mallPayMethodLabel(item) }}</span>
            <span class="sclaims__date">{{ formatDate(item.claimAt || item.orderedAt) }} 요청</span>
          </div>

          <div class="sclaims__item-body">
            <img
              v-if="item.image"
              class="sclaims__img"
              :src="item.image"
              :alt="item.name"
              loading="lazy"
            />
            <div class="sclaims__info">
              <p class="sclaims__name">{{ item.name }}</p>
              <p class="sclaims__meta">
                {{ item.buyerOrg }}
                <template v-if="item.buyerName"> · {{ item.buyerName }}</template>
                · {{ item.qty }}개
              </p>
              <p v-if="item.claimType === 'RETURN' && item.returnCause" class="sclaims__reason">
                {{ item.returnCause === 'CHANGE' ? '단순 변심' : '상품 하자·오배송' }}
              </p>
              <p class="sclaims__reason">
                <AppIcon icon="mdi-message-alert-outline" :size="15" />
                {{ item.claimReason || '사유 없음' }}
              </p>
              <p v-if="isRejected(item)" class="sclaims__reason is-reject">
                <AppIcon icon="mdi-close-circle-outline" :size="15" />
                반려 · {{ item.claimRejectReason }}
              </p>
            </div>
            <div class="sclaims__amount">
              <div class="sclaims__amount-block">
                <span>구매자 환불액</span>
                <strong>{{ formatWon(item.refundAmount || item.amount) }}원</strong>
              </div>
              <div class="sclaims__amount-block is-settle">
                <span>{{ sellerSettleCaption(item) }}</span>
                <em>{{ formatWon(sellerSettleAmount(item)) }}원</em>
              </div>
            </div>
          </div>

          <div v-if="isPending(item.status)" class="sclaims__actions">
            <button
              type="button"
              class="sui-btn sui-btn--sm"
              :disabled="working === item.idx"
              @click="openReject(item)"
            >
              요청 반려
            </button>
            <button
              type="button"
              class="sui-btn sui-btn--sm sui-btn--primary"
              :disabled="working === item.idx"
              @click="onApprove(item)"
            >
              {{ working === item.idx ? '처리 중…' : '요청 승인' }}
            </button>
          </div>
          <form
            v-if="rejectTarget?.idx === item.idx"
            class="sclaims__reject"
            @submit.prevent="submitReject"
          >
            <label>
              <span>반려 사유</span>
              <textarea
                v-model.trim="rejectReason"
                rows="3"
                maxlength="255"
                placeholder="구매 기관에 안내할 반려 사유를 적어 주세요."
              />
            </label>
            <div class="sclaims__reject-actions">
              <button type="button" class="sui-btn sui-btn--sm" @click="closeReject">취소</button>
              <button
                type="submit"
                class="sui-btn sui-btn--sm sui-btn--primary"
                :disabled="working === item.idx || !rejectReason"
              >
                반려 확정
              </button>
            </div>
          </form>
          <p v-else-if="isRejected(item)" class="sclaims__done">
            <AppIcon icon="mdi-close-circle-outline" :size="15" />
            반려된 요청입니다.
            <template v-if="item.claimResolvedAt"> · {{ formatDate(item.claimResolvedAt) }}</template>
          </p>
          <p v-else-if="!isPending(item.status)" class="sclaims__done">
            <AppIcon icon="mdi-check-circle-outline" :size="15" />
            승인 처리된 건입니다.
            <template v-if="item.claimResolvedAt"> · {{ formatDate(item.claimResolvedAt) }}</template>
          </p>
        </li>
      </ul>

      <div v-else class="sui-empty">
        <AppIcon icon="mdi-emoticon-happy-outline" :size="32" />
        <p>{{ loading ? '불러오는 중입니다…' : '처리할 취소 · 반품 요청이 없습니다.' }}</p>
      </div>
    </section>

    <transition name="sui-toast">
      <p v-if="toast" class="sui-toast">
        <AppIcon icon="mdi-check-circle" :size="18" />
        {{ toast }}
      </p>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppIcon from '@/components/common/AppIcon.vue'
import { confirmDialog } from '@/composables/useConfirmDialog'
import { useListDateRange } from '@/composables/useListDateRange'
import { formatWon } from '@/constants/sellerData'
import {
  MALL_ORDER_ITEM_STATUS_LABEL,
  mallPayMethodLabel,
  approveSellerClaim,
  fetchSellerClaims,
  rejectSellerClaim,
  type MallOrderItemStatus,
  type SellerClaimItem,
} from '@/api/mallOrder'

type TabKey = 'pending' | 'CANCEL_REQUEST' | 'RETURN_REQUEST' | 'CANCELED' | 'REJECTED'

const TAB_KEYS: TabKey[] = [
  'pending',
  'CANCEL_REQUEST',
  'RETURN_REQUEST',
  'CANCELED',
  'REJECTED',
]

const route = useRoute()
const router = useRouter()
const items = ref<SellerClaimItem[]>([])
const loading = ref(false)
const errorMessage = ref('')
const working = ref(0)
const toast = ref('')
let toastTimer: ReturnType<typeof setTimeout> | undefined
const rejectTarget = ref<SellerClaimItem | null>(null)
const rejectReason = ref('')

function tabFromQuery(): TabKey {
  const raw = String(route.query.type || route.query.tab || '')
  if (TAB_KEYS.includes(raw as TabKey)) return raw as TabKey
  return 'pending'
}

const activeTab = ref<TabKey>(tabFromQuery())
const keyword = ref(String(route.query.keyword ?? '').trim())
const { fromDate, toDate, preset, presets, applyPreset, onManualDateChange, inRange } =
  useListDateRange('3m')

function setActiveTab(next: TabKey) {
  activeTab.value = next
  const query: Record<string, string> = {}
  if (next !== 'pending') query.type = next
  if (keyword.value.trim()) query.keyword = keyword.value.trim()
  router.replace({
    path: '/seller/claims',
    query,
  })
}

watch(
  () => [route.query.type, route.query.tab, route.query.keyword],
  () => {
    activeTab.value = tabFromQuery()
    keyword.value = String(route.query.keyword ?? '').trim()
  },
)

function isPending(status: MallOrderItemStatus) {
  return status === 'CANCEL_REQUEST' || status === 'RETURN_REQUEST'
}

function isRejected(item: SellerClaimItem) {
  return Boolean(item.claimRejectReason) && !isPending(item.status) && item.status !== 'CANCELED'
}

function statusTone(status: MallOrderItemStatus): string {
  if (status === 'CANCEL_REQUEST') return 'sui-badge--amber'
  if (status === 'RETURN_REQUEST') return 'sui-badge--red'
  return 'sui-badge--gray'
}

async function load() {
  loading.value = true
  errorMessage.value = ''
  try {
    const result = await fetchSellerClaims()
    items.value = result.items
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '목록을 불러오지 못했습니다.'
    items.value = []
  } finally {
    loading.value = false
  }
}

onMounted(load)

const pendingList = computed(() => items.value.filter((item) => isPending(item.status)))

const rejectedList = computed(() => items.value.filter((item) => isRejected(item)))

const tabs = computed(() => [
  { key: 'pending' as TabKey, label: '처리 대기', count: pendingList.value.length },
  {
    key: 'CANCEL_REQUEST' as TabKey,
    label: '취소 요청',
    count: items.value.filter((item) => item.status === 'CANCEL_REQUEST').length,
  },
  {
    key: 'RETURN_REQUEST' as TabKey,
    label: '반품 요청',
    count: items.value.filter((item) => item.status === 'RETURN_REQUEST').length,
  },
  {
    key: 'CANCELED' as TabKey,
    label: '승인 완료',
    count: items.value.filter((item) => item.status === 'CANCELED').length,
  },
  {
    key: 'REJECTED' as TabKey,
    label: '반려',
    count: rejectedList.value.length,
  },
])

const filtered = computed(() => {
  const query = keyword.value.toLowerCase()

  return items.value
    .filter((item) => inRange(item.claimAt || item.claimResolvedAt || item.orderedAt))
    .filter((item) => {
      if (activeTab.value === 'pending') return isPending(item.status)
      if (activeTab.value === 'REJECTED') return isRejected(item)
      if (activeTab.value === 'CANCELED') return item.status === 'CANCELED'
      return item.status === activeTab.value
    })
    .filter((item) => {
      if (!query) return true
      return (
        item.orderCode.toLowerCase().includes(query) ||
        item.name.toLowerCase().includes(query) ||
        item.buyerOrg.toLowerCase().includes(query)
      )
    })
})

const summary = computed(() => {
  const pendingAmount = pendingList.value.reduce((sum, item) => sum + item.amount, 0)
  const done = items.value.filter((item) => item.status === 'CANCELED').length
  const total = items.value.length
  const rate = total ? ((done / total) * 100).toFixed(1) : '0.0'

  return [
    {
      label: '처리 대기',
      value: String(pendingList.value.length),
      unit: '건',
      desc: '승인 또는 반려가 필요합니다',
    },
    {
      label: '환불 예정 금액',
      value: formatWon(pendingAmount),
      unit: '원',
      desc: '승인 시 정산에서 차감됩니다',
    },
    {
      label: '처리 완료 비율',
      value: rate,
      unit: '%',
      desc: '전체 클레임 대비',
    },
    {
      label: '처리 완료',
      value: String(done),
      unit: '건',
      desc: '승인된 취소 · 반품',
    },
  ]
})

function notify(message: string) {
  toast.value = message
  window.clearTimeout(toastTimer)
  toastTimer = setTimeout(() => {
    toast.value = ''
  }, 2400)
}

function sellerSettleCaption(item: SellerClaimItem) {
  if (item.claimType !== 'RETURN' && item.status !== 'RETURN_REQUEST') return '내 정산 차감'
  if (item.returnCause === 'CHANGE') return '받을 반품비'
  return '상품 정산'
}

function sellerSettleAmount(item: SellerClaimItem) {
  if (item.claimType === 'RETURN' || item.status === 'RETURN_REQUEST') {
    if (item.returnCause === 'CHANGE') return Math.max(0, Number(item.appliedReturnFee) || 0)
    return 0
  }
  return item.settlement
}

async function onApprove(item: SellerClaimItem) {
  if (working.value) return
  const isReturn = item.status === 'RETURN_REQUEST'
  const card = item.payMethod === 'TOSS'
  const changeMind = item.returnCause === 'CHANGE'
  const ok = await confirmDialog({
    title: '클레임 승인',
    message: isReturn
      ? changeMind
        ? '단순 변심 반품을 승인할까요? 상품 대금은 정산되지 않고, 반품비만 정산으로 받습니다. 재고는 자동으로 돌아오지 않습니다.'
        : card
          ? '하자·오배송 반품을 승인할까요? 상품 대금은 정산되지 않고, 결제했던 카드·간편결제로 전액 환불됩니다. 재고는 자동으로 돌아오지 않습니다.'
          : '하자·오배송 반품을 승인할까요? 상품 대금은 정산되지 않습니다. 재고는 자동으로 돌아오지 않습니다.'
      : card
        ? '요청을 승인할까요? 결제했던 카드·간편결제로 환불됩니다.'
        : '요청을 승인할까요? 승인하면 정산에서 차감됩니다.',
    detail: [
      item.orderCode,
      item.name,
      isReturn
        ? changeMind
          ? `받을 반품비 ${formatWon(sellerSettleAmount(item))}원`
          : '상품 정산 없음'
        : `정산 차감 ${formatWon(item.settlement)}원`,
      isReturn
        ? `실물 입고 후 상품관리에서 재고를 추가해 주세요.${item.productCode ? `\n(/seller/products?code=${item.productCode})` : ''}`
        : '',
    ]
      .filter(Boolean)
      .join('\n'),
    confirmLabel: '승인',
    tone: 'primary',
  })
  if (!ok) return

  working.value = item.idx
  try {
    const message = await approveSellerClaim(item.idx)
    item.status = 'CANCELED'
    item.claimResolvedAt = new Date().toISOString()
    notify(
      isReturn
        ? `${message} 재고는 상품관리에서 수동으로 반영해 주세요.`
        : message,
    )
  } catch (error) {
    notify(error instanceof Error ? error.message : '승인에 실패했습니다.')
  } finally {
    working.value = 0
  }
}

function openReject(item: SellerClaimItem) {
  rejectTarget.value = item
  rejectReason.value = ''
}

function closeReject() {
  rejectTarget.value = null
  rejectReason.value = ''
}

async function submitReject() {
  if (!rejectTarget.value || working.value || !rejectReason.value) return
  const ok = await confirmDialog({
    title: '클레임 반려',
    message: '이 요청을 반려할까요? 사유가 구매 기관에 전달됩니다.',
    detail: `${rejectTarget.value.orderCode}\n${rejectTarget.value.name}\n사유: ${rejectReason.value}`,
    confirmLabel: '반려',
    tone: 'danger',
  })
  if (!ok) return

  working.value = rejectTarget.value.idx
  try {
    const message = await rejectSellerClaim(rejectTarget.value.idx, rejectReason.value)
    closeReject()
    notify(message)
    await load()
    setActiveTab('REJECTED')
  } catch (error) {
    notify(error instanceof Error ? error.message : '반려에 실패했습니다.')
  } finally {
    working.value = 0
  }
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10)
  return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(
    date.getDate(),
  ).padStart(2, '0')}`
}
</script>

<style lang="scss" src="./SellerClaimsPage.scss"></style>
